// ============================================
// USERS LIST PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { MoreVertical, Eye, Edit, Ban, CheckCircle } from 'lucide-react';
import type { User, UserRole, UserStatus } from '@crew-at-sea/shared-types';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  StatusBadge,
  ConfirmDialog,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatDateTime, getInitials, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/users/')({
  component: UsersPage,
});

function UsersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    user: User | null;
    action: 'suspend' | 'activate';
  }>({ open: false, user: null, action: 'suspend' });

  // Kullanıcıları getir
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['admin', 'users', { page, limit, role: roleFilter, status: statusFilter, search }],
    queryFn: async () => {
      const response = await adminApi.getUsers({
        page,
        limit,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      return (response as any)?.data || response;
    },
  });

  // Veri yapısını normalize et
  const data = rawData ? {
    data: rawData.data || [],
    meta: rawData.meta || { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false }
  } : { data: [], meta: { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false } };

  // Durum güncelleme mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: UserStatus; reason?: string }) =>
      adminApi.updateUserStatus(id, { status, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setConfirmDialog({ open: false, user: null, action: 'suspend' });
    },
  });

  const handleConfirmAction = (reason?: string) => {
    if (!confirmDialog.user) return;
    const newStatus: string =
      confirmDialog.action === 'suspend' ? 'SUSPENDED' : 'ACTIVE';
    updateStatusMutation.mutate({ id: confirmDialog.user.id, status: newStatus as UserStatus, reason });
  };

  const columns = [
    {
      key: 'user',
      header: 'Kullanıcı',
      render: (user: User) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-maritime-100 text-maritime-700 font-medium">
            {getInitials(user.email.split('@')[0])}
          </div>
          <div>
            <p className="font-medium text-gray-900">{user.email}</p>
            <p className="text-xs text-gray-500">ID: {user.id.slice(0, 8)}...</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rol',
      render: (user: User) => {
        const roleLabels: Record<UserRole, string> = {
          SEAFARER: 'Denizci',
          COMPANY: 'Şirket',
          ADMIN: 'Yönetici',
        };
        const roleColors: Record<UserRole, string> = {
          SEAFARER: 'bg-blue-100 text-blue-800',
          COMPANY: 'bg-purple-100 text-purple-800',
          ADMIN: 'bg-red-100 text-red-800',
        };
        return (
          <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full', roleColors[user.role])}>
            {roleLabels[user.role]}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Durum',
      render: (user: User) => <StatusBadge status={user.status} dot />,
    },
    {
      key: 'emailVerified',
      header: 'E-posta Doğrulama',
      render: (user: User) => (
        <span
          className={cn(
            'inline-flex items-center gap-1 text-sm',
            user.emailVerified ? 'text-green-600' : 'text-gray-400'
          )}
        >
          {user.emailVerified ? (
            <>
              <CheckCircle className="h-4 w-4" />
              Doğrulandı
            </>
          ) : (
            'Bekliyor'
          )}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Son Giriş',
      render: (user: User) => (
        <span className="text-gray-500">
          {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '-'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Kayıt Tarihi',
      render: (user: User) => (
        <span className="text-gray-500">{formatDateTime(user.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (user: User) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <Link
                to={`/users/${user.id}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                Görüntüle
              </Link>
              <Link
                to={`/users/${user.id}/edit` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Edit className="h-4 w-4" />
                Düzenle
              </Link>
              <hr className="my-1" />
              {user.status === 'SUSPENDED' ? (
                <button
                  onClick={() =>
                    setConfirmDialog({ open: true, user, action: 'activate' })
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                >
                  <CheckCircle className="h-4 w-4" />
                  Aktifleştir
                </button>
              ) : (
                <button
                  onClick={() =>
                    setConfirmDialog({ open: true, user, action: 'suspend' })
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full"
                >
                  <Ban className="h-4 w-4" />
                  Askıya Al
                </button>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const roleOptions = [
    { value: 'SEAFARER', label: 'Denizci' },
    { value: 'COMPANY', label: 'Şirket' },
    { value: 'ADMIN', label: 'Yönetici' },
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Aktif' },
    { value: 'INACTIVE', label: 'Pasif' },
    { value: 'SUSPENDED', label: 'Askıya Alındı' },
    { value: 'PENDING_VERIFICATION', label: 'Doğrulama Bekliyor' },
  ];

  const activeFiltersCount =
    (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kullanıcılar"
        description="Tüm kullanıcıları yönetin"
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="E-posta ile ara..."
        filters={
          <>
            <FilterSelect
              value={roleFilter}
              onChange={setRoleFilter}
              options={roleOptions}
              placeholder="Tüm Roller"
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder="Tüm Durumlar"
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setRoleFilter('');
          setStatusFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        keyExtractor={(user) => user.id}
        loading={isLoading}
        emptyMessage="Kullanıcı bulunamadı"
        pagination={{
          page,
          limit,
          total: data?.meta.total || 0,
          totalPages: data?.meta.totalPages || 1,
          onPageChange: setPage,
          onLimitChange: setLimit,
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, open })
        }
        title={
          confirmDialog.action === 'suspend'
            ? 'Kullanıcıyı Askıya Al'
            : 'Kullanıcıyı Aktifleştir'
        }
        description={
          confirmDialog.action === 'suspend'
            ? `${confirmDialog.user?.email} kullanıcısını askıya almak istediğinize emin misiniz? Kullanıcı sisteme giriş yapamayacak.`
            : `${confirmDialog.user?.email} kullanıcısını aktifleştirmek istediğinize emin misiniz?`
        }
        variant={confirmDialog.action === 'suspend' ? 'warning' : 'success'}
        confirmText={confirmDialog.action === 'suspend' ? 'Askıya Al' : 'Aktifleştir'}
        onConfirm={handleConfirmAction}
        loading={updateStatusMutation.isPending}
        showReasonInput={confirmDialog.action === 'suspend'}
        reasonPlaceholder="Askıya alma sebebi (opsiyonel)"
      />
    </div>
  );
}
